
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { SupplierModel } from '@/models/supplier';
import { StoreModel } from "@/models/store";
import { ReportModel } from "@/models/report";
import { MeasureModel } from "@/models/measure";
import { DimensionModel } from "@/models/dimension";
import { AnalyticsTypeModel } from "@/models/analytics_type";
import { ParamModel } from "@/models/param";
import { FilterColumnList } from "@/collections/filter_columns";
import { ParamList } from "@/collections/params";
import { MeasureList } from "@/collections/measures";
import ReportFilter from "@/components/ReportFilter.vue";
import { InventoryParam } from "@/models/inventory_param";
import { ProductSegmentModel } from "@/models/product_segment";
import { FilterColumnModel } from "@/models/filter_column";
import { FilterDimensionList } from "@/collections/filter_dimensions";
import ReplenishmentParamDialog from '@/components/ReplenishmentParamDialog.vue';
import { ReplenishmentSettingRecordList } from "@/collections/replenishment_setting_records";
import { EventBus } from "@/main";
import { SupplierProductList } from '@/collections/supplier_products';
import { SupplierProductModel } from '@/models/supplier_product';
import Permission from '@/components/Permission.vue';
import { FEATURES } from '@/config/featureConfig';
import { SortableColumn } from '@/types/sort';
import ReportSort from '@/components/ReportSort.vue';
import moment from 'moment';
import { TaxTypeList } from '@/collections/tax_types';
import { TaxTypeModel } from '@/models/tax_type';
import CurrencySelect from '@/components/CurrencySelect.vue';

@Component({
  components: {
    ReportFilter,
    ReplenishmentParamDialog,
    Permission,
    ReportSort,
    CurrencySelect
  }
})
export default class SupplierDetailView extends Vue {
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;
  @Prop({ default: () => new MeasureList() })
  public measures!: MeasureList;

  // Expose FEATURES to the template
  public FEATURES = FEATURES;

  private supplier: SupplierModel | null = null;
  private loading = false;
  private saving = false;
  private activeTab = 'settings';
  private supplierNotFound = false;
  private errorMessage = '';
  private hasError = false;
  private products: any[] = [];
  private editedSupplier: SupplierModel = new SupplierModel(null);
  private addProductDialog = false;
  private productSearch = '';
  // Search for supplier's existing products (different from Add Product dialog search)
  private supplierProductsSearch = '';
  private supplierProductsSearchTimeout: ReturnType<typeof setTimeout> | null = null;
  private selectedProducts: any[] = [];
  private selectedSupplierProducts: any[] = [];
  private availableProducts: any[] = [];
  private confirmUnlinkDialog = false;
  private productToUnlink: any = null;
  private settingsValid = true;
  private statusOptions = ['ACTIVE', 'INACTIVE', 'SUSPENDED'];
  private loadingProduct: boolean = false;
  private loadingAvailableProduct: boolean = false;
  private addingProducts: boolean = false;
  private taxTypeList: TaxTypeList = new TaxTypeList();
  private reportColumns: any[] = [];
  private filterData: any = {
    dimension: new DimensionModel(),
    analyticsType: new AnalyticsTypeModel(),
    dates: [],
    filterColumns: new FilterColumnList(),
    reportParams: new ParamList(),
    productSegment: new ProductSegmentModel(),
  };
  private measure: MeasureModel = new MeasureModel({code: 'replenishment_settings'});
  private dimension: DimensionModel = new DimensionModel({code: 'by_variant'});
  private filterColumns: FilterColumnList = new FilterColumnList();
  private paramDialogVisible = false;
  private paramDialogMode: 'bulk' | 'individual' = 'individual';
  private currentParams = new InventoryParam();
  private updatingProductId: string | null = null;
  private currentlyEditingVariantId: string | null = null;
  private replenishmentSettingRecords: ReplenishmentSettingRecordList = new ReplenishmentSettingRecordList();
  private isHistoryLoading: boolean = false;
  private originalParams: {[key: string]: InventoryParam} = {}; // For tracking changes
  private supplierProducts = new SupplierProductList();
  private pagination = {
    page: 1,
    itemsPerPage: 20,
    totalProducts: 0,
    pageSizeOptions: [20, 50, 100, 200],
    sortBy: [] as string[],
    sortDesc: [] as boolean[],
  };
  private availableProductsPagination = {
    page: 1,
    itemsPerPage: 20,
    totalItems: 0,
    sortBy: [],
    sortDesc: [],
    pageSizeOptions: [20, 50, 100],
    groupBy: [],
    groupDesc: [],
    multiSort: false,
    mustSort: false
  };
  private isAvailableProductsLoading = false;
  private confirmBulkUnlinkDialog = false;
  private bulkRemoveLoading = false;
  private isSelectAllAvailableProducts = false;
  private isEditMode = false; // Track if we're in edit mode for settings

  private get isActive(): boolean {
    return this.editedSupplier.status === 'ACTIVE';
  }

  private set isActive(value: boolean) {
    this.editedSupplier.status = value ? 'ACTIVE' : 'INACTIVE';
  }

  private handleStatusChange(value: boolean): void {
    this.editedSupplier.status = value ? 'ACTIVE' : 'INACTIVE';
  }

  private setupReorderParameters() {
    this.$router.push(`/settings/reorder-parameters?tab=product&supplier=${encodeURIComponent(this.supplier?.name || '')}`);
  }

  public get filterReportColumns() {
    let reportColumns: any = [
            {
              "name": "Product",
              "code": "Item[Item Description]",
              "dataType": "text",
              "luisMapping": "by_product",
              "description": "",
              "filterable": true,
              "sortable": true,
              "original_key": "Item[Item Description]",
              "colorIndex": false,
              "autoSelect": false
          },
          {
              "name": "Variant",
              "code": "variant",
              "dataType": "text",
              "luisMapping": "variant",
              "description": "",
              "filterable": true,
              "sortable": true,
              "original_key": "variant",
              "colorIndex": false,
              "autoSelect": false
          },
          {
              "name": "SKU",
              "code": "product_sku",
              "dataType": "text",
              "luisMapping": "by_product_sku",
              "description": "",
              "filterable": true,
              "sortable": true,
              "original_key": "product_sku",
              "colorIndex": false,
              "autoSelect": false
          },
          {
              "name": "Barcode",
              "code": "product_barcode",
              "dataType": "text",
              "luisMapping": "product_barcode",
              "description": "",
              "filterable": true,
              "sortable": true,
              "original_key": "product_barcode",
              "colorIndex": false,
              "autoSelect": false
          },
          {
              "name": "Vendor",
              "code": "Vendor Name",
              "dataType": "text",
              "luisMapping": "by_vendor",
              "description": "",
              "filterable": true,
              "sortable": true,
              "original_key": "Vendor Name",
              "colorIndex": false,
              "autoSelect": false
          },
          {
              "name": "Variant Id",
              "code": "variant_id",
              "dataType": "text",
              "luisMapping": "variant_id",
              "description": "",
              "filterable": true,
              "sortable": true,
              "original_key": "variant_id",
              "colorIndex": false,
              "autoSelect": false
          },
          {
              "name": "Product Id",
              "code": "product_id",
              "dataType": "text",
              "luisMapping": "product_id",
              "description": "",
              "filterable": true,
              "sortable": true,
              "original_key": "product_id",
              "colorIndex": false,
              "autoSelect": false
          },
          {
              "name": "Product Type",
              "code": "product_type",
              "dataType": "text",
              "luisMapping": "by_product_type",
              "description": "",
              "filterable": true,
              "sortable": true,
              "original_key": "product_type",
              "colorIndex": false,
              "autoSelect": false
          },
          {
              "name": "Product Status",
              "code": "product_status",
              "dataType": "text",
              "luisMapping": "by_product_status",
              "description": "",
              "filterable": true,
              "sortable": true,
              "original_key": "product_status",
              "colorIndex": false,
              "autoSelect": false
          },
          {
              "name": "Product Tags",
              "code": "product_tags",
              "dataType": "text",
              "luisMapping": "by_product_tag",
              "description": "",
              "filterable": true,
              "sortable": true,
              "original_key": "product_tags",
              "colorIndex": false,
              "autoSelect": false
          },
          {
              "name": "Collections",
              "code": "product_collections",
              "dataType": "text",
              "luisMapping": "product_collections",
              "description": "",
              "filterable": true,
              "sortable": true,
              "original_key": "product_collections",
              "colorIndex": false,
              "autoSelect": false
          },
    ];
    // if (this.measure.filterDimensions.size() > 0) {
    //   for (const dim of this.measure.filterDimensions.toArray()) {
    //     reportColumns.push({
    //       name: dim.label ? dim.label : dim.name,
    //       code: dim.fieldCode,
    //       dataType: dim.dataType,
    //       luisMapping: dim.code,
    //     });
    //   }
    // }

    // for (const column of this.reportColumns) {
    //   reportColumns.push(column);
    // }
    
    reportColumns = this.refineFilterColumns(reportColumns);
    return reportColumns;
  }

  public refineFilterColumns(reportColumns: any) {
    const duplicateColumns: any = {
      "Item[Item Category]": "product_collections",
      product_tag: "product_tags",
      order_tag: "order_tags",
      customer_tag: "customer_tags",
    };

    for (const column of reportColumns) {
      if (Object.keys(duplicateColumns).includes(column.code)) {
        let index: number = 0;
        for (const column2 of reportColumns) {
          if (column2.code === duplicateColumns[column.code]) {
            reportColumns.splice(index, 1);
            break;
          }
          index++;
        }
      }
    }
    return reportColumns;
  }

  // Add validation rules
  private emailRules = [
    (v: string) => !!v || 'Email is required',
    (v: string) => /.+@.+\..+/.test(v) || 'Email must be valid'
  ];
  private phoneRule = (v: string) => !v || /^\+?[\d\s-]+$/.test(v) || 'Phone number must be valid';
  private requiredNumberRule = (v: number) => !!v || 'Field is required';
  private positiveNumberRule = (v: number) => v >= 0 || 'Value must be positive';
  private percentageRule = (v: number) => !v || (v >= 0 && v <= 100) || 'Percentage must be between 0 and 100';
  private maxValueRule = (v: number) => v >= this.editedSupplier.minOrderValue || 'Must be greater than minimum order value';
  private maxInventoryValueRule = (v: number) => v >= this.editedSupplier.minInventoryValue || 'Must be greater than minimum inventory value';
  private requiredRule = (v: string) => !!v || 'Field is required';
  private productHeaders = [
    { text: '', value: 'data-table-select', width: '50px' },
    { text: 'Image', value: 'image', width: '80px' },
    { text: 'Product', value: 'Item[Item Description]' },
    { text: 'Variant', value: 'variant' },
    { text: 'SKU', value: 'product_sku' },
    { text: 'Barcode', value: 'product_barcode' },
    { text: 'Vendor', value: 'Vendor Name' },
    { text: 'Supplier', value: 'supplier' },
    { text: 'Unit Cost', value: 'unit_cost' },
    { text: 'Updated At', value: 'updatedAt', sortable: false, width: '150px' },
    {
      text: 'Actions',
      value: 'actions',
      sortable: false,
      width: '200px'
    }
  ];

  private availableProductHeaders = [
    { text: 'Id', value: 'variant_id', sortable: false, width: '100px' },
    { text: 'Product', value: 'Item[Item Description]' },
    { text: 'Variant', value: 'variant' },
    { text: 'SKU', value: 'product_sku' },
    { text: 'Barcode', value: 'product_barcode' },
    { text: 'Vendor', value: 'Vendor Name' },
    { text: 'Supplier', value: 'supplier' },
    { text: 'Updated At', value: 'updatedAt', sortable: false, width: '150px' },
    // { text: 'Lead time', value: '[vendor_lead_time]' },
    // { text: 'Unit Cost', value: 'unit_cost' },
  ];

  private maxQuantityRule = (v: number): boolean | string => 
    !v || v >= this.editedSupplier.minOrderQuantity || 'Must be greater than minimum order quantity';

  private scopeOptions = [
    { text: 'Per Product', value: 'product' },
    { text: 'Per Variant', value: 'variant' }
  ];

  async created() {
    try {
      await this.fetchSupplier();
      if (this.supplier && this.supplier.id) {
        // Set default scope to variant if not set
        if (!this.supplier.scope) {
          this.supplier.scope = 'variant';
        }
        await Promise.all([
          this.fetchProducts(),
          this.taxTypeList.fetch().catch((e: any) => console.warn('Tax types unavailable:', e)),
        ]);
      } else {
        console.warn('No supplier data available after loading');
      }
    } catch (error) {
      console.error('Error in created hook:', error);
      this.$emit('error', 'Failed to initialize supplier details');
    }
  }

  beforeDestroy() {
    // Clean up search timeouts to prevent memory leaks
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
    if (this.supplierProductsSearchTimeout) {
      clearTimeout(this.supplierProductsSearchTimeout);
      this.supplierProductsSearchTimeout = null;
    }
  }

  public async fetchProducts() {
    this.loadingProduct = true;
    
    try {
      if (this.supplier && this.supplier.id) {
        // Prepare options for optimized fetch
        const options: any = {
          page: this.pagination.page,
          limit: this.pagination.itemsPerPage,
          search: this.supplierProductsSearch || '', // Use supplier products search
        };

        // Handle sorting
        if (this.pagination.sortBy && this.pagination.sortBy.length > 0) {
          options.sortBy = this.pagination.sortBy[0];
          options.sortOrder = this.pagination.sortDesc && this.pagination.sortDesc[0] ? 'desc' : 'asc';
        }

        // Use the new optimized method that calls the fast backend API
        const result = await this.supplier.fetchProductsOptimized(options);

        // Update pagination with results
        this.pagination.totalProducts = result.pagination.total;

        // Map products data to match the existing component structure
        this.products = result.products.map((item: any) => ({
          variant_id: item.variant_id || item.variantId,
          image: item.image || item.imageUrl || '',
          'Item[Item Description]': item['Item[Item Description]'] || item.product_name || item.productName || '',
          variant: item.variant || item.variant_title || item.variantTitle || '',
          product_sku: item.product_sku || item.sku || '',
          product_barcode: item.product_barcode || item.barcode || '',
          'Vendor Name': item['Vendor Name'] || item.vendor || '',
          supplier: item.supplier || item.supplier_name || this.supplier?.name,
          'unit_cost': item.unit_cost || item.cost || 0,
          unitCost: item.unit_cost || item.cost || 0,
          // Map supplier-specific settings
          '[vendor_lead_time]': item.lead_time || item['[vendor_lead_time]'] || 0,
          '[min_stock_cover_days]': item.min_stock_cover_days || item['[min_stock_cover_days]'] || 0,
          '[max_stock_cover_days]': item.max_stock_cover_days || item['[max_stock_cover_days]'] || 0,
          '[min_inventory_quantity]': item.min_inventory_quantity || item['[min_inventory_quantity]'] || 0,
          '[max_inventory_quantity]': item.max_inventory_quantity || item['[max_inventory_quantity]'] || 0,
          '[min_order_quantity]': item.minimum_order_quantity || item.min_order_quantity || item['[min_order_quantity]'] || 0,
          '[max_order_quantity]': item.max_order_quantity || item['[max_order_quantity]'] || 0,
          '[fixed_order_quantity]': item.fix_order_quantity || item.fixed_order_quantity || item['[fixed_order_quantity]'] || 0,
          '[reorder_cycle]': item.order_cycle || item.reorder_cycle || item['[reorder_cycle]'] || 0,
          '[replenishment_exclusion]': item.replenishment_exclusion || item['[replenishment_exclusion]'] || 0,
          updatedAt: item.updatedAt || null,
        }));
      }
    } catch (error) {
      console.error('Error loading products:', error);
      this.$emit('error', 'Failed to load products');
    } finally {
      this.loadingProduct = false;
    }
  }

  // Computed property for sortable columns configuration
  public get sortableColumns(): SortableColumn[] {
    return [
      {
        code: 'Item[Item Description]',
        name: 'Product Name',
        dataType: 'text',
        sortable: true
      },
      {
        code: 'variant',
        name: 'Variant',
        dataType: 'text',
        sortable: true
      },
      {
        code: 'product_sku',
        name: 'SKU',
        dataType: 'text',
        sortable: true
      },
      {
        code: 'product_barcode',
        name: 'Barcode',
        dataType: 'text',
        sortable: true
      },
      {
        code: 'Vendor Name',
        name: 'Vendor',
        dataType: 'text',
        sortable: true
      },
      {
        code: 'unit_cost',
        name: 'Unit Cost',
        dataType: 'currency',
        sortable: true
      }
    ];
  }

  private async fetchSupplier() {
    this.loading = true;
    // Reset error states
    this.supplierNotFound = false;
    this.hasError = false;
    this.errorMessage = '';
    
    try {
      const { id } = this.$route.params;
      if (!id) {
        throw new Error('Supplier ID is required');
      }

      // Create new supplier model and fetch details
      this.supplier = new SupplierModel({ id: parseInt(id, 10) });
      await this.supplier.getSupplierDetails();
      
      // Set default scope to variant if not already set
      if (!this.supplier.scope) {
        this.supplier.scope = 'variant';
      }
      
      // Create a copy for editing
      this.editedSupplier = new SupplierModel(this.supplier);
    } catch (error: any) {
      console.error('Error loading supplier:', error);
      
      // Check if it's a 404 error (supplier not found)
      if (error?.response?.status === 404 || error?.status === 404) {
        this.supplierNotFound = true;
      } else {
        this.hasError = true;
        this.errorMessage = error?.message || 'Failed to load supplier details';
        this.$emit('error', this.errorMessage);
      }
    } finally {
      this.loading = false;
    }
  }

  private formatNumber(value: number | undefined): string {
    if (!value) return '0';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  private getStatusColor(metrics: any[] | undefined): string {
    if (!metrics) return 'grey';
    const fillRate = metrics.find(m => m.label === 'Order Fill Rate');
    if (!fillRate) return 'grey';
    const value = parseFloat(fillRate.value);
    if (value >= 95) return 'success';
    if (value >= 90) return 'warning';
    return 'error';
  }

  private getSupplierStatus(metrics: any[] | undefined): string {
    if (!metrics) return 'Unknown Performance';
    const fillRate = metrics.find(m => m.label === 'Order Fill Rate');
    if (!fillRate) return 'Unknown Performance';
    const value = parseFloat(fillRate.value);
    if (value >= 95) return 'Excellent';
    if (value >= 90) return 'Good';
    return 'Needs Improvement';
  }

  private getMetricTrendColor(metric: any): string {
    if (!metric.trend) return 'grey';
    return metric.trend.isPositive ? 'success' : 'error';
  }

  private getMetricTrendIcon(metric: any): string {
    if (!metric.trend) return 'mdi-minus';
    return metric.trend.isPositive ? 'mdi-arrow-up' : 'mdi-arrow-down';
  }

  private getStockLevelColor(item: any): string {
    const stock = item.currentStock;
    if (stock <= 0) return 'error';
    if (stock <= 10) return 'warning';
    return 'success';
  }

  private getProductMetricColor(metrics: any): string {
    if (metrics.demand_variability === 'High') return 'error';
    if (metrics.demand_variability === 'Medium') return 'warning';
    return 'success';
  }

  private async saveSettings() {
    // Force validation of the form
    if (this.$refs.settingsForm) {
      (this.$refs.settingsForm as any).validate();
    }

    if (!this.settingsValid || !this.supplier) return;
    this.saving = true;
    try {
      await this.editedSupplier.update();
      this.isEditMode = false; // Return to view mode after successful save
      // The editedSupplier now has the updated data from backend response
      // Create a new SupplierModel instance to ensure all properties are properly mapped
      this.supplier = new SupplierModel(this.editedSupplier);
      EventBus.$emit("show-snackbar", {
        message: "Settings updated successfully",
        color: "success",
      });
    } catch (error) {
      EventBus.$emit("show-snackbar", {
        message: "Failed to update settings",
        color: "error",
      });
    } finally {
      this.saving = false;
    }
  }

  private getDefaultTaxTypeName(): string {
    const taxId = this.supplier?.options?.financial?.default_tax_type_id;
    if (!taxId) return 'Not set';
    const taxType = this.taxTypeList.items.find((t: TaxTypeModel) => t.id == taxId);
    return taxType ? `${taxType.name} (${(taxType.rate * 100).toFixed(1)}%)` : 'Not set';
  }

  // Add methods to toggle between view and edit modes
  private enableEditMode() {
    // Make sure the editedSupplier has the latest data
    this.editedSupplier = new SupplierModel(this.supplier);

    // Set default scope to variant if not set
    if (!this.editedSupplier.scope) {
      this.editedSupplier.scope = 'variant';
    }

    // Ensure nested options.financial path exists for binding (use $set for Vue 2 reactivity)
    if (!this.editedSupplier.options) this.$set(this.editedSupplier, 'options', {});
    if (!this.editedSupplier.options.financial) this.$set(this.editedSupplier.options, 'financial', {});

    this.isEditMode = true;
    // Set initial validation state to true, will be updated by form validators
    this.settingsValid = true;
    
    // Allow time for the form to render before initializing validation
    this.$nextTick(() => {
      if (this.$refs.settingsForm) {
        (this.$refs.settingsForm as any).resetValidation();
      }
    });
  }

  private cancelEditMode() {
    // Reset form data to original values
    this.editedSupplier = new SupplierModel(this.supplier);
    this.isEditMode = false;
  }

  private async showAddProductDialog() {
    this.addProductDialog = true;
    this.clearAvailableProductSelection(); // Reset selection state
    
    // Reset search field
    this.productSearch = '';
    
    // Reset pagination when opening dialog
    this.availableProductsPagination = {
      page: 1,
      itemsPerPage: 10,
      totalItems: 0,
      sortBy: [],
      sortDesc: [],
      pageSizeOptions: [10, 20, 50, 100],
      groupBy: [],
      groupDesc: [],
      multiSort: false,
      mustSort: false
    };
    this.selectedProducts = [];
    await this.fetchAvailableProducts();
  }

  private async addSelectedProducts() {
    if (!this.supplier) return;
    this.addingProducts = true;
    let response: any;
    try {
      this.addingProducts = true;
      
      // Call API
      if (this.isSelectAllAvailableProducts) {
        response = await this.supplier.addAllProducts(this.selectedProducts, this.filterData);
      } else {
        response = await this.supplier.addProducts(this.selectedProducts, this.filterData);
      }
      // Close dialog immediately after API call
      await this.fetchProducts();
      if (response?.data?.created > 0) {
        EventBus.$emit("show-snackbar", {
          message: `Updated successfully, ${response?.data?.created || 0} product(s) added, ${response?.data?.skipped || 0} product(s) skipped, ${response?.data?.failed || 0} product(s) failed`,
          color: "success",
          timeout: 3000
        });
      } else {
        EventBus.$emit("show-snackbar", {
          message: `Products belong to other supplier, ${response?.data?.created || 0} product(s) added, ${response?.data?.skipped || 0} product(s) skipped, ${response?.data?.failed || 0  } product(s) failed`,
          color: "warning",
          timeout: 5000
        });
      }
    } catch (error) {
      console.error('Error adding products:', error);
      EventBus.$emit("show-snackbar", {
        message: "Failed to add products",
        color: "error",
        timeout: 5000
      });
    } finally {
      this.addingProducts = false;
      this.addProductDialog = false; // Close dialog
      this.clearAvailableProductSelection(); // Reset selection state
    }
  }

  private showUnlinkConfirmation(product: any) {
    this.productToUnlink = product;
    this.confirmUnlinkDialog = true;
  }

  private async confirmUnlinkProduct() {
    if (!this.productToUnlink) return;
    this.confirmUnlinkDialog = false;
    await this.unlinkProduct(this.productToUnlink);
    this.productToUnlink = null;
  }

  private async unlinkProduct(product: any) {
    if (!this.supplier) return;
    
    try {
      // Remove the product from the local state
      this.products = this.products.filter(p => p.variant_id !== product.variant_id);
      
      //update the available products by add the product back to the unassigned products
      this.availableProducts.push(product);
      // call to remove products from supplier
      await this.supplier.removeProducts([product]);

      const productName = product['Item[Item Description]'] || product.product_name || product.productName || product.product_sku || 'Product';
      EventBus.$emit("show-snackbar", {
        message: `Unlinked ${productName} from supplier`,
        color: "success",
      });
    } catch (error) {
      EventBus.$emit("show-snackbar", {
        message: "Failed to unlink product from supplier",
        color: "error",
      });
    }
  }

  private getChipColor(value: number, trend: 'up' | 'down'): string {
    if (trend === 'up') {
      return value > 10 ? 'error' : 'success';
    }
    return value > 10 ? 'warning' : 'success';
  }

  // Add the validation check
  private get canViewSupplierDetail(): boolean {
    const validateValues = [
      {
        code: 'supplier_management',
        value: 'unlocked'
      }
    ];
    const validateResult = this.store.validateRule(validateValues);
    return validateResult.isValidate;
  }

  // Add watcher for filterData
  @Watch('filterData', { deep: true })
  async onFilterDataChange() {
    if (this.addProductDialog) {
      this.clearAvailableProductSelection(); // Reset selection on filter change
      // Reset to first page when filters change
      this.availableProductsPagination.page = 1;
      await this.fetchAvailableProducts();
    }
  }

  // Add new method for fetching available products
  private async fetchAvailableProducts() {
    // If already loading, skip this request
    if (this.isAvailableProductsLoading) {
      return;
    }
    
    this.loadingAvailableProduct = true;
    this.isAvailableProductsLoading = true;

    try {
      if (this.supplier && this.supplier.id) {
        // Prepare options for the fetch
        const options = {
          initialFilterColumns: this.filterData.filterColumns,
          pageOptions: {
            page: this.availableProductsPagination.page,
            itemsPerPage: Number(this.availableProductsPagination.itemsPerPage),
            sortBy: this.availableProductsPagination.sortBy,
            sortDesc: this.availableProductsPagination.sortDesc
          },
          productSegment: this.filterData.productSegment,
          //supplierIdColumn: 'supplier_ids', // use supplier_ids to filter products not belong to the current supplier
          columns: [{ code: 'variant_id', dataType: 'number', name: 'Variant ID' }]
        };

        // Get total count and data in parallel
        const [countResponse, reportModel] = await Promise.all([
          this.supplier.countProductsWithOptions(
            'replenishment_settings',
            'null',
            options 
          ),
          this.supplier.fetchProductsWithOptions(
            'replenishment_settings',
            'null',
            options
          )
        ]);

        // Update pagination total only if we got new data
        if (countResponse?.totalItems !== undefined) {
          this.availableProductsPagination.totalItems = Number(countResponse.totalItems);
        }

        // Store report columns for filters
        this.reportColumns = reportModel.getReportColumnList();
        
        // Get variant-level settings
        this.availableProducts = reportModel.getReportRowsData();
      }
    } catch (error) {
      console.error('Error loading available products:', error);
      EventBus.$emit("show-snackbar", {
        message: "Failed to load available products",
        color: "error",
      });
    } finally {
      this.loadingAvailableProduct = false;
      this.isAvailableProductsLoading = false;
    }
  }


  // Optimized method for fetching available products using new Shopify GraphQL API
  private async fetchAvailableProductsOptimized() {
    // If already loading, skip this request
    if (this.isAvailableProductsLoading) {
      return;
    }
    
    this.loadingAvailableProduct = true;
    this.isAvailableProductsLoading = true;

    try {
      if (this.supplier && this.supplier.id) {
        // Prepare options for the new searchAvailableVariants API
        const options: any = {
          page: this.availableProductsPagination.page,
          limit: Number(this.availableProductsPagination.itemsPerPage),
          search: this.productSearch || '', // Use search term if available
        };

        // Use the new optimized searchAvailableVariants method
        // This uses Shopify GraphQL for fast product discovery
        const result = await this.supplier.searchAvailableVariants(options);

        // Update pagination with results
        this.availableProductsPagination.totalItems = result.pagination.total;

        // Map products data to match the existing component structure
        this.availableProducts = result.variants.map((item: any) => ({
          variant_id: item.variant_id || item.variantId,
          image: item.image || item.imageUrl || '',
          'Item[Item Description]': item['Item[Item Description]'] || item.product_name || item.productName || '',
          variant: item.variant || item.variant_title || item.variantTitle || '',
          product_sku: item.product_sku || item.sku || '',
          product_barcode: item.product_barcode || item.barcode || '',
          'Vendor Name': item['Vendor Name'] || item.vendor || '',
          supplier: item.currentSupplier || item.supplier_name || '', // Show current supplier if assigned
          suppliers: item.suppliers || [], // Array of all suppliers for this variant
          isAssigned: item.isAssigned || false, // Whether variant is already assigned to current supplier
          unit_cost: item.unit_cost || item.cost || 0,
          // Include other fields that might be needed
          '[vendor_lead_time]': item.lead_time || item['[vendor_lead_time]'] || 0,
          '[min_order_quantity]': item.minimum_order_quantity || item.min_order_quantity || 0,
          '[max_order_quantity]': item.max_order_quantity || 0,
          '[fixed_order_quantity]': item.fix_order_quantity || item.fixed_order_quantity || 0,
          '[reorder_cycle]': item.order_cycle || item.reorder_cycle || 0,
        }));
      }
    } catch (error) {
      console.error('Error loading available products:', error);
      EventBus.$emit("show-snackbar", {
        message: "Failed to load available products",
        color: "error",
      });
    } finally {
      this.loadingAvailableProduct = false;
      this.isAvailableProductsLoading = false;
    }
  }


  private async handlePageChange(options: any) {
    // Update pagination values
    this.pagination.page = options.page;
    this.pagination.itemsPerPage = options.itemsPerPage;
    this.pagination.sortBy = options.sortBy || [];
    this.pagination.sortDesc = options.sortDesc || [];
    
    // Refetch products with new pagination
    await this.fetchProducts();
  }

  private async handleAvailableProductsPageChange(options: any) {
    // Check if the options actually changed
    if (
      this.availableProductsPagination.page === options.page &&
      this.availableProductsPagination.itemsPerPage === options.itemsPerPage &&
      JSON.stringify(this.availableProductsPagination.sortBy) === JSON.stringify(options.sortBy) &&
      JSON.stringify(this.availableProductsPagination.sortDesc) === JSON.stringify(options.sortDesc)
    ) {
      return;
    }

    // Update pagination values
    this.availableProductsPagination.page = options.page;
    this.availableProductsPagination.itemsPerPage = options.itemsPerPage;
    this.availableProductsPagination.sortBy = options.sortBy;
    this.availableProductsPagination.sortDesc = options.sortDesc;
    
    // Fetch available products with new pagination
    await this.fetchAvailableProducts();
  }

  private showBulkUnlinkConfirmation() {
    if (this.selectedSupplierProducts.length > 0) {
      this.confirmBulkUnlinkDialog = true;
    }
  }

  private async confirmBulkUnlinkProducts() {
    if (!this.supplier || this.selectedSupplierProducts.length === 0) return;
    
    this.bulkRemoveLoading = true;
    this.confirmBulkUnlinkDialog = false;
    try {
      // Remove the products from the local state
      const selectedIds = this.selectedSupplierProducts.map(p => p.variant_id);
      this.products = this.products.filter(p => !selectedIds.includes(p.variant_id));
      
      // Call to remove products from supplier
      await this.supplier.removeProducts(this.selectedSupplierProducts);

      // Clear selection
      this.selectedSupplierProducts = [];
      
      EventBus.$emit("show-snackbar", {
        message: `Successfully removed ${selectedIds.length} products from supplier`,
        color: "success",
      });
    } catch (error) {
      console.error('Error in bulk remove:', error);
      // Refresh the products list to ensure consistency
      await this.fetchProducts();
      
      EventBus.$emit("show-snackbar", {
        message: "Failed to remove products from supplier",
        color: "error",
      });
    } finally {
      this.bulkRemoveLoading = false;
      this.confirmBulkUnlinkDialog = false;
    }
  }

  private selectAllAvailableProducts() {
    this.isSelectAllAvailableProducts = true;
  }

  private clearAvailableProductSelection() {
    this.isSelectAllAvailableProducts = false;
    this.selectedProducts = [];
  }

  private isAvailableProductSelectable(item: any): boolean {
    // Items are only selectable if "Select All" is not active
    return !this.isSelectAllAvailableProducts;
  }

  private closeAddProductDialog() {
    this.addProductDialog = false;
    this.clearAvailableProductSelection(); // Reset selection state on close
  }

  private createPurchaseOrder() {
    if (this.supplier && this.supplier.id) {
      this.$router.push({ 
        name: 'createPurchaseOrder', // Corrected route name
        params: { supplierId: this.supplier.id.toString() } 
      });
    } else {
      EventBus.$emit("show-snackbar", {
        message: "Cannot create Purchase Order: Supplier details not loaded.",
        color: "error",
      });
    }
  }

  // Handler for ReportSort component sort changes
  public onSortChanged(event: { sortBy: string[], sortDesc: boolean[] }): void {
    this.pagination.sortBy = event.sortBy;
    this.pagination.sortDesc = event.sortDesc;
    this.fetchProducts();
  }

  // Debounced search handler for Add Product Modal
  private searchTimeout: any = null;
  
  private onProductSearchInput(value: string): void {
    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Debounce search by 500ms to avoid too many API calls
    this.searchTimeout = setTimeout(async () => {
      // Reset to first page when searching
      this.availableProductsPagination.page = 1;
      
      // Clear selection when searching
      this.clearAvailableProductSelection();
      
      // Fetch with new search term
      await this.fetchAvailableProducts();
    }, 500);
  }

  /**
   * Handle search input for supplier products list (debounced)
   */
  private onSupplierProductsSearchInput(value: string): void {
    // Clear existing timeout
    if (this.supplierProductsSearchTimeout) {
      clearTimeout(this.supplierProductsSearchTimeout);
    }

    // Debounce search by 500ms to avoid too many API calls
    this.supplierProductsSearchTimeout = setTimeout(async () => {
      // Reset to first page when searching
      this.pagination.page = 1;

      // Clear selection when searching
      this.selectedSupplierProducts = [];

      // Fetch with new search term
      await this.fetchProducts();
    }, 500);
  }

  /**
   * Clear search and reset product list
   */
  private async clearSupplierProductsSearch(): Promise<void> {
    this.supplierProductsSearch = '';

    // Clear timeout if exists
    if (this.supplierProductsSearchTimeout) {
      clearTimeout(this.supplierProductsSearchTimeout);
    }

    // Reset to first page
    this.pagination.page = 1;

    // Clear selection
    this.selectedSupplierProducts = [];

    // Fetch all products
    await this.fetchProducts();
  }

  /**
   * Format datetime to YYYY-MM-DD HH:mm format
   */
  private formatDateTime(dateTime: string | null | undefined): string {
    if (!dateTime) {
      return '-';
    }
    return moment(dateTime).format('YYYY-MM-DD HH:mm');
  }
}
